import { Input, Modal, Radio, Select, Space } from 'antd'
import React, { useEffect, useState } from 'react'
import useStationListStore from '@/stores/stationList'
import { useTranslation } from 'react-i18next'
import { Station } from '@/type/stationType'
import { SHARE_TYPE, STATION_AUTH, areaCodeLists } from '@/utils/const'
import usePersistDataStore from '@/stores/persistData'
import { setStationShare } from '@/api/powerStation'
import useMessage from '@/hooks/useMessage'

const ShareModal: React.FC<{ open: boolean; stationId: number; onClose: () => void }> = ({
  open,
  stationId,
  onClose,
}) => {
  const { t } = useTranslation()
  const lang = usePersistDataStore(state => state.lang)
  const { message } = useMessage()
  const [stationState, setStationState] = useState<Station | undefined>(undefined)
  const station = useStationListStore(state => state.station)
  const stationInit = useStationListStore(state => state.stationInit)

  const [grantAuth, setAuth] = useState(0)
  const [shareMethod, setShareMethod] = useState(SHARE_TYPE.EMAIL)
  const [accountPhonePrefix, setAccountPhonePrefix] = useState('+61,12')
  const [account, setAccount] = useState('')

  const [grantAuthOptions, setGrantAuthOptions] = useState<{ label: string; value: number; show: boolean }[]>([])
  const [accountPhoneOptions, setAccountPhoneOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    const targetStation = station.find(item => item.id === stationId)
    if (targetStation) {
      setStationState(targetStation)
      setGrantAuthOptions([
        { label: t('site.viewOnly'), value: STATION_AUTH.VIEW, show: true }, // 查看
        { label: t('site.editOnly'), value: STATION_AUTH.EDIT, show: true }, // 编辑
        { label: t('site.fullControl'), value: STATION_AUTH.MANAGE, show: true }, // 管理
        { label: t('site.transferAuth'), value: STATION_AUTH.TRANSFER, show: true }, // 转移
      ])
      setAccountPhoneOptions(
        areaCodeLists.map((item, index) => {
          if (lang === 'en_US') {
            return { label: item.value + item.en, value: item.value + ',' + index }
          } else if (lang === 'zh_CN') {
            return { label: item.value + item.cn, value: item.value + ',' + index }
          } else {
            return { label: item.value + 'Unknown Country', value: item.value + ',' + index }
          }
        })
      )
    }
  }, [stationId, t])

  const confirmShare = async () => {
    if (account.trim() === '') {
      message.error(t('site.emptySharer'))
      return
    }
    const data = {
      station_id: stationId,
      station_share_user: account,
      station_share_type: grantAuth,
      share_account_type: shareMethod,
      area_code: undefined as string | undefined,
    }
    if (shareMethod === SHARE_TYPE.PHONE) {
      data.area_code = accountPhonePrefix.split(',')[0]
    }
    const res: any = await setStationShare().catch((error: any) => {
      console.log(error)
      message.error(error.message || error.msg || error)
    })
    if (res.code !== 1001) {
      message.error(res.msg)
    } else {
      message.success(t('site.shareSuccess'))
      if (grantAuth === STATION_AUTH.TRANSFER) {
        stationInit().catch(e => {
          console.log(e)
          message.error(e)
        })
      }
    }
  }

  return (
    <Modal
      title={t('site.share') + ' ' + stationState?.station_name}
      open={open}
      onCancel={onClose}
      onOk={confirmShare}
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
      centered>
      <div className='shareModalContent'>
        <div className='title'>{t('site.grantAuth')}</div>
        <Radio.Group value={grantAuth} onChange={e => setAuth(e.target.value)}>
          <Space direction='vertical'>
            {grantAuthOptions.map(item => {
              if (item.show) {
                return (
                  <Radio key={item.value} value={item.value}>
                    {item.label}
                  </Radio>
                )
              } else {
                return <></>
              }
            })}
          </Space>
        </Radio.Group>
        <div className='shareSeparator'></div>
        <div className='title'>{t('site.shareMethod')}</div>
        <Radio.Group value={shareMethod} onChange={e => setShareMethod(e.target.value)}>
          <Radio value={SHARE_TYPE.EMAIL}>{t('site.email')}</Radio>
          <Radio value={SHARE_TYPE.PHONE}>{t('site.phone')}</Radio>
        </Radio.Group>
        <div className='shareTo'>
          <div className='title'>{t('site.to')}</div>
          {shareMethod === SHARE_TYPE.PHONE && (
            <Select
              options={accountPhoneOptions}
              value={accountPhonePrefix}
              onChange={value => setAccountPhonePrefix(value)}
            />
          )}
          <Input value={account} onChange={e => setAccount(e.target.value)} />
        </div>
      </div>
    </Modal>
  )
}

export default ShareModal
